import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
// import { State, City } from "country-state-city"; // Import the country-state-city package
import axiosInstance from "../Help/AxiosLogicInstance";
import stateCodeMap from "../data/stateCodes.json";
import Popup from './Popup.jsx';
import { useAuth } from ".././hook/useAuth";
const MAX_TOTAL_UPLOAD_SIZE = 10 * 1024 * 1024;

// The reported type varies by browser and OS, so the extension is also accepted
const isZipFile = (file) =>
  file.type === "application/zip" ||
  file.type === "application/x-zip-compressed" ||
  file.name.toLowerCase().endsWith(".zip");

const ScholarshipForm = () => {
  // Handle submit
  // ...inside ScholarshipForm component...
  const navigate = useNavigate();
  // State for form fields
  const StudentId = localStorage.getItem('StudentId') || '';
  const [loading, setLoading] = useState(false); // Add loading state
  const isSubmittingRef = useRef(false); // Reference to track submission state

  // Get all Indian states using the package
  // const indianStates = State.getStatesOfCountry("IN");

  // Add state for district options
  const [domicileDistrictOptions, setDomicileDistrictOptions] = useState([]);
  const [collegeDistrictOptions, setCollegeDistrictOptions] = useState([]);
  const [statesData, setStatesData] = useState([]);
  const [stateCodes, setStateCodes] = useState({});
  const [indianStates, setIndianStates] = useState([]);

  useEffect(() => {
    document.title = "Scholarship Application - MedEngage";
  }, []);
  useEffect(() => {
    const loadStatesData = async () => {
      try {
        const response = await fetch('/Districts/states_districts.csv');
        const text = await response.text();
        const lines = text.split('\n').slice(1); // Skip header
        const data = lines.map(line => {
          const [state, district] = line.split(',').map(s => s.replace(/"/g, '').trim());
          return { state, district };
        }).filter(item => item.state && item.district);
        setStatesData(data);
        const uniqueStates = [...new Set(data.map(item => item.state))].sort();
        setIndianStates(uniqueStates);
        // Use the official codes from the JSON map when available
        setStateCodes(stateCodeMap || {});
      } catch (error) {
        console.error('Error loading states data:', error);
      }
    };
    loadStatesData();
  }, []);
  // Application active flag — if false, don't allow opening the form
  const [isScholarshipActive, setIsScholarshipActive] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchSetting = async () => {
      try {
        const res = await axiosInstance.get('/api/Student/GetApplicationSettingMasterByFormId/5');
        const isActive = res?.data?.Data?.[0]?.IsActive;
        if (mounted) setIsScholarshipActive(isActive === undefined ? true : !!isActive);
      } catch (err) {
        console.warn('Failed to fetch scholarship application setting', err);
        // Keep default true on error to avoid blocking accidentally
      }
    };
    fetchSetting();
    return () => { mounted = false; };
  }, []);
  const [domicileCert, setDomicileCert] = useState(null);
  const [incomeCert, setIncomeCert] = useState(null);
  const [identity1, setIdentity1] = useState(null);
  const [identity2, setIdentity2] = useState(null);
  const [passportFile, setPassportFile] = useState(null);
  const [disabilityCert, setDisabilityCert] = useState(null);
  const [step, setStep] = useState(1);
  const [collegeId, setCollegeId] = useState(null);
  const [marksheet, setMarksheet] = useState(null);
  const [internationalPublicationFile, setInternationalPublicationFile] = useState(null);
  const [nationalPublicationFile, setNationalPublicationFile] = useState(null);
  const [presentationFile, setPresentationFile] = useState(null);

  const [extraCurricularFile, setExtraCurricularFile] = useState([]);

  const [bankDocs, setBankDocs] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [signature, setSignature] = useState(null);
  // 1. Change state initialization
  const [communityServiceFile, setCommunityServiceFile] = useState([]);
  // ...existing code...

  // Update the state for awardsFile
  const [awardsFile, setAwardsFile] = useState([]);

  // Yes/No answer controlling each optional document. A document on a "no" answer
  // is disabled and can't be replaced, so it doesn't count towards the limit.
  const conditionalFileAnswers = {
    internationalPublicationFile: "internationalPublication",
    nationalPublicationFile: "nationalPublication",
    presentationFile: "presentation",
    awardsFile: "awards",
    extraCurricularFile: "extraCurricular",
    communityServiceFile: "communityService",
    disabilityCert: "differentlyAbled",
  };

  // Sizes (in bytes) of the documents currently attached, keyed by field name
  const uploadSizesRef = useRef({});

  const toMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

  // Combined size of the documents that will be submitted
  const getUploadedTotal = (excludedField) =>
    Object.entries(uploadSizesRef.current).reduce(
      (total, [field, size]) =>
        field === excludedField || form[conditionalFileAnswers[field]] === "no" ? total : total + size,
      0
    );

  // Checks the combined 10 MB limit and reserves the file's size. Replacing a
  // field's file only counts the new one. Sizes are reserved before uploading so
  // that uploads running at the same time can't exceed the limit together.
  const reserveUploadSize = (e, file, fieldName) => {
    const otherFilesTotal = getUploadedTotal(fieldName);
    if (otherFilesTotal + file.size > MAX_TOTAL_UPLOAD_SIZE) {
      const message = `Total size of all uploaded documents cannot exceed 10 MB. This file is ${toMB(file.size)} MB, but only ${toMB(MAX_TOTAL_UPLOAD_SIZE - otherFilesTotal)} MB is available. Please upload a smaller file.`;
      toast.error(message);
      e.target.value = "";
      // Deferred because the inputs' onChange clears this field's error right after calling the handler
      queueMicrotask(() => setErrors((prev) => ({ ...prev, [fieldName]: message })));
      return false;
    }
    uploadSizesRef.current[fieldName] = file.size;
    return true;
  };

  // Add this handler after handleCommunityServiceFileChange
  const handleAwardsFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!isZipFile(file)) {
        toast.error("Please upload documents as a ZIP file");
        e.target.value = "";
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("ZIP file must be ≤ 10MB");
        e.target.value = "";
        return;
      }

      const previousSize = uploadSizesRef.current.awardsFile;
      if (!reserveUploadSize(e, file, "awardsFile")) {
        return;
      }

      try {
        // Show loading state
     

        // Upload file and get URL
        const fileUrl = await uploadFileToS3(file);

        // Store the URL in an array format for consistency
        setAwardsFile([fileUrl]);

        // Clear any previous errors
        setErrors((prev) => ({ ...prev, awardsFile: "" }));

      } catch (error) {
        // Set field-specific error message
        uploadSizesRef.current.awardsFile = previousSize || 0;
        setErrors((prev) => ({ ...prev, awardsFile: "Upload failed. Please upload again." }));
        e.target.value = "";
      }
    }
  };

  // ...existing code...


  // Add this after handleCommunityServiceFileChange
  const handleExtraCurricularFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!isZipFile(file)) {
        toast.error("Please upload documents as a ZIP file");
        e.target.value = "";
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("ZIP file must be ≤ 10MB");
        e.target.value = "";
        return;
      }

      const previousSize = uploadSizesRef.current.extraCurricularFile;
      if (!reserveUploadSize(e, file, "extraCurricularFile")) {
        return;
      }

      try {
        // Show loading state
      

        // Upload file and get URL
        const fileUrl = await uploadFileToS3(file);

        // Store the URL in an array format for consistency
        setExtraCurricularFile([fileUrl]);

        // Clear any previous errors
        setErrors((prev) => ({ ...prev, extraCurricularFile: "" }));

       
      } catch (error) {
        // Set field-specific error message
        uploadSizesRef.current.extraCurricularFile = previousSize || 0;
        setErrors((prev) => ({ ...prev, extraCurricularFile: "Upload failed. Please upload again." }));
        e.target.value = "";
      }
    }
  };
  // Handler
  const handleCommunityServiceFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!isZipFile(file)) {
        toast.error("Please upload documents as a ZIP file");
        e.target.value = "";
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("ZIP file must be ≤ 10MB");
        e.target.value = "";
        return;
      }

      const previousSize = uploadSizesRef.current.communityServiceFile;
      if (!reserveUploadSize(e, file, "communityServiceFile")) {
        return;
      }

      try {
        // Show loading state
       

        // Upload file and get URL
        const fileUrl = await uploadFileToS3(file);

        // Store the URL in an array format for consistency
        setCommunityServiceFile([fileUrl]);

        // Clear any previous errors
        setErrors((prev) => ({ ...prev, communityServiceFile: "" }));


      } catch (error) {
        // Set field-specific error message
        uploadSizesRef.current.communityServiceFile = previousSize || 0;
        setErrors((prev) => ({ ...prev, communityServiceFile: "Upload failed. Please upload again." }));
        e.target.value = "";
      }
    }
  };
  // Handler for ZIP files - now uploads immediately
  const handleZipFileChange = async (e, setter, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (!isZipFile(file)) {
        toast.error("Only ZIP files are allowed for this upload.");
        e.target.value = "";
        setErrors((prev) => ({ ...prev, [fieldName]: "Please upload a ZIP file" }));
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // Allow up to 10MB for ZIP
        toast.error("ZIP file must be ≤ 10MB");
        e.target.value = "";
        setErrors((prev) => ({ ...prev, [fieldName]: "Please upload a ZIP file smaller than 10MB" }));
        return;
      }
      const previousSize = uploadSizesRef.current[fieldName];
      if (!reserveUploadSize(e, file, fieldName)) {
        return;
      }

      try {
        // Show loading state
        setErrors((prev) => ({ ...prev, [fieldName]: "Uploading..." }));

        // Upload file and get URL
        const fileUrl = await uploadFileToS3(file);

        // Store the URL
        setter(fileUrl);

        // Clear any previous errors
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));

      
      } catch (error) {
        // Set field-specific error message that will show below the input
        uploadSizesRef.current[fieldName] = previousSize || 0;
        setErrors((prev) => ({ ...prev, [fieldName]: "Upload failed. Please upload again." }));
        e.target.value = "";
        toast.error("File upload failed. Please try again.");
      }
    }
  };

  // File change handler - now uploads immediately
  const handleFileChange = async (e, setter, fieldName) => {
    const file = e.target.files[0];
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf", "application/zip", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error("File must be ≤ 10MB");
      e.target.value = ""; // Clear the file input so user can re-select
      setErrors((prev) => ({ ...prev, [fieldName]: "Please upload a file smaller than 10MB" }));
      return;
    }
    if (file && !validTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPG, PNG, PDF, DOC, DOCX, ZIP allowed");
      e.target.value = "";
      setErrors((prev) => ({ ...prev, [fieldName]: "Please upload a valid file type" }));
      return;
    }

    if (file) {
      const previousSize = uploadSizesRef.current[fieldName];
      if (!reserveUploadSize(e, file, fieldName)) {
        return;
      }

      try {
        // Show loading state
        setErrors((prev) => ({ ...prev, [fieldName]: "Uploading..." }));

        // Upload file and get URL
        const fileUrl = await uploadFileToS3(file);

        // Store the URL instead of the file object
        setter(fileUrl);

        // Clear any previous errors
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));

      
      } catch (error) {
        // Set field-specific error message that will show below the input
        uploadSizesRef.current[fieldName] = previousSize || 0;
        setErrors((prev) => ({ ...prev, [fieldName]: "Upload failed. Please upload again." }));
        e.target.value = ""; // Clear the file input
        toast.error("File upload failed. Please try again.");
      }
    }
  };
  const identityProofOptions = [
    { value: "aadhaar", label: "Aadhaar Card" },
    { value: "passport", label: "Passport" },
    { value: "voter", label: "Voter ID Card" },
    { value: "driving", label: "Driving License" },
    { value: "other", label: "Other" },
  ];
  const [form, setForm] = useState({
    receivedScholarship: "",
    useOfFunds: "",
    siblingsScholarship: "",
    siblingOneName: "",
    siblingOneCollege: "",
    siblingTwoName: "",
    siblingTwoCollege: "",
    siblingThreeName: "",
    siblingThreeCollege: "",
    applicantName: "",
    identityProof: [],
    gender: "",
    dob: "",
    mobile: "",
    email: "",
    fatherName: "",
    motherName: "",
    address: "",
    stateOfDomicile: "",
    districtOfDomicile: "",
    annualIncome: "",
    differentlyAbled: "no",
    singleParent: "no",
    parentsAlive: "yes",
    alternateMobile: "",
    guardianMobile: "",
    
    // Step 2 fields
    medicalCollege: "",
    stateOfCollege: "",
    districtOfCollege: "",
    cityOfCollege: "",
    medicalCourse: "",
    yearOfLearning: "",
    totalMarks: "",
    marksAchieved: "",
    neetMarks: "",
    
    internationalPublication: "",
    internationalPublicationLinks: "",
    nationalPublication: "",
    nationalPublicationLinks: "",
    presentation: "",
    awards: "",
    extraCurricular: "",
    communityService: "",
    
    // Bank details
    bankName: "",
    branchName: "",
    accountNumber: "",
    ifscCode: "",
    bankAddress: "",
    panNumber: "",
    
    // Social media
    facebook: "",
    instagram: "",
    linkedin: "",
    twitter: "",
    
    // Declaration
    declaration: false,
    declarationVerification: false,
    signatureDeclaration: false,
    
    // For conditional fields
    yearOfWinning: "",
    yearOfWinningOther: "",
    fundUtilization: "",
    useOfFundsOther: ""
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (form.stateOfDomicile && statesData.length > 0) {
      const districts = statesData
        .filter(item => item.state === form.stateOfDomicile)
        .map(item => item.district)
        .sort();
      setDomicileDistrictOptions(districts);
    } else {
      setDomicileDistrictOptions([]);
    }
  }, [form.stateOfDomicile, statesData]);
  useEffect(() => {
    if (form.stateOfCollege && statesData.length > 0) {
      const districts = statesData
        .filter(item => item.state === form.stateOfCollege)
        .map(item => item.district)
        .sort();
      setCollegeDistrictOptions(districts);
    } else {
      setCollegeDistrictOptions([]);
    }
  }, [form.stateOfCollege, statesData]);
  // State for errors
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Helper function to upload file to S3 via API
  const uploadFileToS3 = async (file) => {
    if (!file) return "";

    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Upload to S3 via API
      const response = await axiosInstance.post('/api/Student/UploadScholarshipFile', {
        FileBase64: base64
      });

      if (response.data.Status === "200") {
        return response.data.FileUrl;
      } else {
        throw new Error("Upload failed. Please upload again.");
      }
    } catch (error) {
      console.error("File upload error:", error);
      // Don't show toast here - let individual handlers show field-specific errors
      throw new Error("Upload failed. Please upload again.");
    }
  };

  // Helper function to upload multiple files
  const uploadMultipleFilesToS3 = async (files) => {
    if (!files || files.length === 0) return [];

    const uploadPromises = files.map(file => uploadFileToS3(file));
    return await Promise.all(uploadPromises);
  };
  
  // Handle input blur - trim spaces and show toast
  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed !== value) {
        // Update form with trimmed value
        setForm(prev => ({ ...prev, [name]: trimmed }));
        // Show field-specific toast message
        // toast.warning(`Leading or trailing spaces removed from ${name}`);
      }
    }
    
    // Run validation after trimming
    setSubmitted(true);
    validate();
  };
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    let updatedValue;

    if (type === "checkbox") {
      updatedValue = checked;
    } else if (type === "file") {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png"];
      if (file && file.size > 10 * 1024 * 1024) {
        toast.error("File must be ≤ 10MB");
        e.target.value = ""; // Clear the file input so user can re-select
        return;
      }
      if (file && !validTypes.includes(file.type)) {
        toast.error("Invalid file type. Only JPG, PNG allowed");
        e.target.value = ""; // Clear the file input so user can re-select
        return;
      }
      updatedValue = file;
    } 
    
    //   // Check for leading or trailing spaces in string values
    //   if (typeof value === 'string' && value.trim() !== value) {
    //     updatedValue = value.trim();
    //     toast.info(`Leading/trailing spaces removed from ${name}`);
    //   } 
    else {
        updatedValue = value;
      }
    

    // Special handling for state of domicile
    if (name === "stateOfDomicile") {
      setForm((prev) => ({
        ...prev,
        [name]: updatedValue,
        districtOfDomicile: "", // Reset district when state changes
      }));
    }
    // Special handling for state of college
    else if (name === "stateOfCollege") {
      setForm((prev) => ({
        ...prev,
        [name]: updatedValue,
        districtOfCollege: "", // Reset district when state changes
      }));
    }
    else {
      setForm((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };


  // Add this check at the start of your return statement

  // Validation function
  const validate = (stepToValidate = step) => {
    console.log(`Validating step ${stepToValidate}`);
    const newErrors = {};

    const phoneRegex = /^(\+91\s?)?\d{10}$/;
    const nameRegex = /^[A-Za-z ]+$/;  // Only letters and spaces
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const addressRegex = /^[a-zA-Z0-9\s,.\/-]+$/;
    if (stepToValidate === 1) {
        Object.keys(form).forEach(key => {
      if (typeof form[key] === 'string' && form[key].trim() !== form[key]) {
        // if (!newErrors[key]) { // Only add if no other error
        //   newErrors[key] = "No leading or trailing spaces allowed";
        // }
      }
    });
      if (!form.receivedScholarship) newErrors.receivedScholarship = "Required";
      if (
        form.receivedScholarship === "yes" &&
        form.yearOfWinning === "Other" &&
        !form.yearOfWinningOther.trim()
      ) {
        newErrors.yearOfWinningOther = "Required";
      }

      // Sibling validation when siblingsScholarship is "2"
      if (form.siblingsScholarship === "2") {
        if (!form.siblingOneName) newErrors.siblingOneName = "Required";
        if (!form.siblingOneCollege) newErrors.siblingOneCollege = "Required";
        if (!form.siblingTwoName) newErrors.siblingTwoName = "Required";
        if (!form.siblingTwoCollege) newErrors.siblingTwoCollege = "Required";
      }


      if (!form.useOfFunds) newErrors.useOfFunds = "Required";
      // if (!form.siblingsScholarship) newErrors.siblingsScholarship = "Required";
      if (!form.applicantName) newErrors.applicantName = "Required";
      if (!form.gender) newErrors.gender = "Required";
      if (!form.dob) newErrors.dob = "Required";
      if (!form.mobile) newErrors.mobile = "Required";
      if (!form.email) newErrors.email = "Required";
      // Enhanced email validation
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (form.email) {
        const trimmedEmail = form.email.trim();
        if (!emailRegex.test(trimmedEmail)) {
          newErrors.email = "Invalid email format (e.g., abc@test.com)";
        }
        // Check for spaces within the email
        if (trimmedEmail.includes(' ')) {
          newErrors.email = "Email cannot contain spaces";
        }
      }
      if (!form.identityProof || form.identityProof.length < 2) newErrors.identityProof = "Required";
      if (!identity1) newErrors.identity1 = "Required";
      if (!identity2) newErrors.identity2 = "Required";
      if (!form.fatherName) newErrors.fatherName = "Required";
      if (!form.motherName) newErrors.motherName = "Required";
      if (!form.address) newErrors.address = "Required";
      if (!passportFile) newErrors.passportFile = "Required";
      if (!form.stateOfDomicile) newErrors.stateOfDomicile = "Required";
      if (!form.districtOfDomicile) newErrors.districtOfDomicile = "Required";
      if (!domicileCert) newErrors.domicileCert = "Required";
      if (!form.annualIncome) newErrors.annualIncome = "Required";

      if (!incomeCert) newErrors.incomeCert = "Required";

      if (form.differentlyAbled === "yes" && !disabilityCert) newErrors.disabilityCert = "Required";
      const phoneRegex = /^(\+91\s?)?\d{5}\s?\d{5}$/;
      if (form.mobile && !phoneRegex.test(form.mobile)) {
        newErrors.mobile = "Use: +91 91522 11510 or 9152211510";
      }
      if (form.alternateMobile && form.alternateMobile.length > 0 && !phoneRegex.test(form.alternateMobile)) {
        newErrors.alternateMobile = "Use: +91 91522 11510 or 9152211510";
      } if (form.address && !/^[a-zA-Z0-9\s,.\-\/#&()\[\]]+$/.test(form.address)) {
        newErrors.address = "Address can only contain letters, numbers, spaces, and basic punctuation (,.-/#&()[])";
      }
      if (form.guardianMobile && form.guardianMobile.length > 0 && !phoneRegex.test(form.guardianMobile)) {
        newErrors.guardianMobile = "Use: +91 91522 11510 or 9152211510";
      }

      // Passport size photo file type/size check
     
      const nameFields = [
        "applicantName",
        "fatherName",
        "motherName",
        // "branchName",
        // "bankName",
        "siblingOneName",
        "siblingTwoName",
        "siblingThreeName",
        "siblingOneCollege",
        "siblingTwoCollege",
        "siblingThreeCollege",





        // Add other name-like fields as needed
      ];
      nameFields.forEach((field) => {
        if (form[field] && !nameRegex.test(form[field].trim())) {
          newErrors[field] = "Only letters and spaces allowed";
        }
      });
      // Pincode validation (if you have a pincode field)
      if (form.pincode && !/^\d{6}$/.test(form.pincode)) {
        newErrors.pincode = "Pincode should be exactly 6 digits";
      }
    }
    if (form.dob) {
      const dobDate = new Date(form.dob);
      const today = new Date();
      if (dobDate > today) {
        newErrors.dob = "Date of birth cannot be in the future";
      }
    }
    if (stepToValidate === 2) {
      if (!form.medicalCollege) newErrors.medicalCollege = "Required";
      if (!form.stateOfCollege) newErrors.stateOfCollege = "Required";
      if (!form.districtOfCollege) newErrors.districtOfCollege = "Required";
      // if (!form.cityOfCollege) newErrors.cityOfCollege = "Required";
      if (!form.medicalCourse) newErrors.medicalCourse = "Required";
      if (!form.yearOfLearning) newErrors.yearOfLearning = "Required";
      if (!collegeId) newErrors.collegeId = "Required";
      if (!form.totalMarks) {
        newErrors.totalMarks = "Required";
      } else if (!/^\d+(\.\d+)?$/.test(form.totalMarks.trim()) || Number(form.totalMarks) <= 0 || Number(form.totalMarks) > 2000) {
        newErrors.totalMarks = "Enter total marks as a number from 1 to 2000, e.g. 1000 or 1250.5";
      }
  if (form.yearOfLearning === "1") {
    const neet = String(form.neetMarks || "").trim();
    if (!neet) newErrors.neetMarks = "Required";
    else if (!/^\d+(\.\d+)?$/.test(neet) || Number(neet) <= 0)
      newErrors.neetMarks = "NEET marks must be a number greater than 0";
    else if (!newErrors.totalMarks && Number(neet) > Number(form.totalMarks))
      newErrors.neetMarks = "NEET marks cannot exceed total NEET marks";
  }
  // Accept either the uploaded marksheet URL stored in `marksheet` state
  // or any value present on form.marksheet. Previously we only checked
  // `form.marksheet` which meant uploaded files (stored in `marksheet`)
  // still caused a 'Required' error.
  if (!marksheet && !form.marksheet) newErrors.marksheet = "Required";
      if (form.yearOfLearning !== "1" && !form.marksAchieved) newErrors.marksAchieved = "Required";
      // International Publication
      if (!form.internationalPublication) newErrors.internationalPublication = "Required";
      if (form.internationalPublication === "yes" && !internationalPublicationFile) newErrors.internationalPublicationFile = "Required";
      if (form.internationalPublication === "yes" && !form.internationalPublicationLinks) newErrors.internationalPublicationLinks = "Required";
      // National Publication
      if (!form.nationalPublication) newErrors.nationalPublication = "Required";
      if (form.nationalPublication === "yes" && !nationalPublicationFile) newErrors.nationalPublicationFile = "Required";
      if (form.nationalPublication === "yes" && !form.nationalPublicationLinks) newErrors.nationalPublicationLinks = "Required";
      // Presentation
      if (!form.presentation) newErrors.presentation = "Required";
      if (form.presentation === "yes" && !presentationFile) newErrors.presentationFile = "Required";
      // Academic/Non-Academic Awards
      if (!form.awards) newErrors.awards = "Required";
      if (form.awards === "yes" && (!awardsFile || awardsFile.length === 0)) newErrors.awardsFile = "Required";
      // Extra-Curricular Achievements
      if (!form.extraCurricular) newErrors.extraCurricular = "Required";
      if (form.extraCurricular === "yes" && (!extraCurricularFile || extraCurricularFile.length === 0)) newErrors.extraCurricularFile = "Required";
      // Community Service
      if (!form.communityService) newErrors.communityService = "Required";
      if (form.communityService === "yes" && (!communityServiceFile || communityServiceFile.length === 0)) newErrors.communityServiceFile = "Required";
      // Bank Details
      if (!form.bankName) newErrors.bankName = "Required";
      if (!form.branchName) newErrors.branchName = "Required";
      if (!form.accountNumber) newErrors.accountNumber = "Required";
      if (!form.ifscCode) newErrors.ifscCode = "Required";
      if (!form.bankAddress) newErrors.bankAddress = "Required";
      if (!bankDocs) newErrors.bankDocs = "Required";
      if (!panFile) newErrors.panFile = "Required";
      if (!form.panNumber) newErrors.panNumber = "Required";
      if (!signature) newErrors.signature = "Required";
      // Declaration checkboxes
      if (!form.declaration) newErrors.declaration = "Required";
      if (!form.declarationVerification) newErrors.declarationVerification = "Required";
      if (!form.signatureDeclaration) newErrors.signatureDeclaration = "Required";
      // if (form.panNumber && form.panNumber.length > 0 && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.panNumber)) {
      //   newErrors.panNumber = "PAN should be in format ABCDE1234F";
      // }
       if (!form.declaration) {
      newErrors.declaration = "You must confirm this declaration";
    }
     if (!form.declarationVerification) {
      newErrors.declarationVerification = "You must confirm this declaration";
    }
     if (!form.signatureDeclaration) {
      newErrors.signatureDeclaration = "You must confirm this declaration";
    }
      // if (form.totalMarks) {
      //   const totalMarksNum = parseFloat(form.totalMarks);
      //   if (totalMarksNum > 1000) {
      //     newErrors.totalMarks = "Total marks cannot exceed 1000";
      //   }
      // }

      // 7. Fix marks achieved validation
      if (form.yearOfLearning !== "1" && form.marksAchieved) {
        const marksAchieved = Number(form.marksAchieved);
        const totalMarks = Number(form.totalMarks) || 0;

        if (isNaN(marksAchieved) || marksAchieved <= 0) {
          newErrors.marksAchieved = "Marks achieved must be greater than 0";
        } else if (marksAchieved > totalMarks) {
          newErrors.marksAchieved = "Marks achieved cannot exceed total marks";
        }
      }
      // IFSC validation
      // if (form.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifscCode)) {
      //   newErrors.ifscCode = "IFSC should be 4 letters, 0, then 6 digits/letters";
      // }
      // Branch Name: only alphabets and spaces
      // if (form.branchName && !/^[A-Za-z ]+$/.test(form.branchName)) {
      //   newErrors.branchName = "Only alphabets and spaces allowed";
      // }
      // Name as in Bank: only alphabets and spaces
      // if (form.bankName && !/^[A-Za-z ]+$/.test(form.bankName)) {
      //   newErrors.bankName = "Only alphabets and spaces allowed";
      // }
      // if (form.bankAddress && !/^[a-zA-Z0-9\s,.\-\/#&()\[\]]+$/.test(form.bankAddress)) {
      //   newErrors.bankAddress = "Address can only contain letters, numbers, spaces, and basic punctuation (,.-/#&()[])";
      // }
      // College Name: alphanumeric and spaces only
      if (form.medicalCollege && !/^[A-Za-z .,'()-]+$/.test(form.medicalCollege.trim())) {
        newErrors.medicalCollege = "Only letters, spaces and . , ' ( ) - allowed";
      }
      // District of College: only letters and spaces
      if (form.districtOfCollege && !nameRegex.test(form.districtOfCollege.trim())) {
        newErrors.districtOfCollege = "Only letters and spaces allowed";
      }
      // Proposed Budget (if present): numbers and commas only
      if (form.proposedBudget && !/^\d+(,\d{3})*(\.\d{1,2})?$/.test(form.proposedBudget)) {
        newErrors.proposedBudget = "Only numbers and commas allowed";
      }
      // Study Start/End Date (if present)
      if (form.startDate && form.endDate && form.endDate < form.startDate) {
        newErrors.endDate = "End Date must be greater than or equal to Start Date";
      }
      // if (form.accountNumber) {
      //   if (!/^\d+$/.test(form.accountNumber)) {
      //     newErrors.accountNumber = "Account number should contain only digits";
      //   }
      // }
      const nameFields = [
        "cityOfCollege",
        "siblingOneCollege",
        "siblingTwoCollege",
        "siblingThreeCollege",

        // Add other name-like fields as needed
      ];
      nameFields.forEach((field) => {
        if (form[field] && !nameRegex.test(form[field].trim())) {
          newErrors[field] = "Only letters and spaces allowed";
        }
      });
      if (form.publishedGrantURL && !/^https?:\/\/[^\s$.?#].[^\s]*$/.test(form.publishedGrantURL)) {
        newErrors.publishedGrantURL = "Enter a valid URL";
      }
      
      // Validate all links and URLs don't have leading/trailing spaces
      const linkFields = ["facebook", "instagram", "linkedin", "twitter", "internationalPublicationLinks", "nationalPublicationLinks"];
      linkFields.forEach(field => {
        if (form[field] && typeof form[field] === 'string' && form[field].trim() !== form[field]) {
         
        }
      });
    }

    return newErrors;
  };

  // function isEligibleForScholarship(form) {
  //   // Count applicant's own wins
  //   let ownWins = form.receivedScholarship === "yes" ? 1 : 0;
  //   // Count siblings' wins
  //   let siblingWins = 0;
  //   if (form.siblingsScholarship === "1") siblingWins = 1;
  //   if (form.siblingsScholarship === "2") siblingWins = 2;
  //   // If you have a third sibling field, add logic for "3"
  //   if (form.siblingsScholarship === "3") siblingWins = 3;
  //   // Total wins
  //   const totalWins = ownWins + siblingWins;
  //   return totalWins <= 2;
  // }
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    
    // Prevent multiple submissions
    if (loading ) {

      return;
    }
    
    // Trim any spaces in string fields before validation
    const formWithTrimmedValues = {};
    Object.keys(form).forEach(key => {
      if (typeof form[key] === 'string') {
        formWithTrimmedValues[key] = form[key].trim();
        // If we trimmed the value, update the form
        if (formWithTrimmedValues[key] !== form[key]) {
          setForm(prev => ({
            ...prev,
            [key]: formWithTrimmedValues[key]
          }));
        }
      } else {
        formWithTrimmedValues[key] = form[key];
      }
    });
    
    // Run validation for the current step
    const validationErrors = validate(step);
    setErrors(validationErrors);
    
    // For debugging
    console.log("Current step:", step);
    console.log("Validation errors:", validationErrors);
    
    if (
      step === 2 &&
      Object.keys(validationErrors).length === 0 &&
      getUploadedTotal() > MAX_TOTAL_UPLOAD_SIZE
    ) {
      toast.error(
        `Total size of all uploaded documents is ${toMB(getUploadedTotal())} MB, which is over the 10 MB limit. Please replace a document with a smaller file.`
      );
      return;
    }

    // Only set submitting flag if we're on step 2 and about to submit
    if (step === 2 && Object.keys(validationErrors).length === 0) {
      isSubmittingRef.current = true; // Mark as submitting
    }
    //  if (!isEligibleForScholarship(form)) {
    //   toast.error("You are not eligible: You or your siblings have already won more than two times.");
    //   return;
    // }

    if (Object.keys(validationErrors).length === 0) {
      if (step === 1) {
        // Trim all string values before moving to step 2
        const trimmedForm = {};
        let hadSpaces = false;
        
        Object.keys(form).forEach(key => {
          if (typeof form[key] === 'string') {
            if (form[key].trim() !== form[key]) {
              trimmedForm[key] = form[key].trim();
              hadSpaces = true;
            }
          }
        });
        
        if (hadSpaces) {
          setForm(prev => ({
            ...prev,
            ...trimmedForm
          }));
        }
        
        setStep(2);
      } else {
        setLoading(true); // Start loading state
        try {
          // Files are already uploaded and URLs are stored in state variables
          // No need to process files to base64 - use the stored URLs directly

      const payload = {
  // Add the missing request field that the API requires
  request: true,
  
  // Rest of your payload fields...
  Type: "",
  CollegeName: form.medicalCollege || "",
  Publications: form.internationalPublication || "",
  PublicationsFile: form.internationalPublication === "no" ? "" : internationalPublicationFile || "",
  NationalJournals: form.nationalPublication || "",
  NationalJournalsFile: form.nationalPublication === "no" ? "" : nationalPublicationFile || "",
  PaperPresentation: form.presentation || "",
  PaperPresentationFile: form.presentation === "no" ? "" : presentationFile || "",
  PosterPresentation: "",
  PosterPresentationFile: "",
  ExtraAchievements: form.extraCurricular || "",
  ExtraAchievementsFile: form.extraCurricular === "no" ? "" : extraCurricularFile.join(",") || "",
  AwardsScholarships: form.awards || "",
  Qualification: form.medicalCourse || "",
  SuperspecialityFile: "",
  FellowshipFile: "",
  CategoryName: "",
  CategoryEssay: "",
  IncomeCert: incomeCert || "",
  ProofFile: "",
  CategoryFinalYearIdCardFile: collegeId || "",
  CategoryMarksheetAcademicYearFile: marksheet || "",
  CategoryFile: "",
  CreatedAt: new Date().toISOString(),
  UpdatedAt: new Date().toISOString(),
  PartFill: 0,
  StudentId: StudentId || "",
  Steps: step,
  AwardsFile: form.awards === "no" ? "" : awardsFile.join(",") || "",
  FacebookDetails: form.facebook || "",
  InstagramDetails: form.instagram || "",
  LinkedinDetails: form.linkedin || "",
  TwitterDetails: form.twitter || "",
  ProgramDetails: form.programDetails || "",
  OtherProgramDetails: form.otherProgramDetails || "",
  CommunityService: form.communityService || "",
  CommunityServiceFile: form.communityService === "no" ? "" : communityServiceFile.join(",") || "",
  FirstYearNeet: form.neetMarks || "",
  FirstYearNeetFile: "",
  ExternalAwardFile: "",
  SyMarks: form.marksAchieved || "",
  SyMarksFile: "",
  ProposalBreakupFile: "",
  ExistingBenifit: form.receivedScholarship || "",
  BenifitDetails: form.fundUtilization || "",
  FundUtilise: form.useOfFunds || "",
  SiblingCount: form.siblingsScholarship || "",
  Yearofwinning: form.yearOfWinning === "Other" ? form.yearOfWinningOther.trim() : form.yearOfWinning || "",
  SiblingThreeCollege: form.siblingThreeCollege || "",
  SiblingThreeName: form.siblingThreeName || "",
  SiblingTwoCollege: form.siblingTwoCollege || "",
  SiblingTwoName: form.siblingTwoName || "",
  SiblingOneName: form.siblingOneName || "",
  SiblingOneCollege: form.siblingOneCollege || "",
  ApplicantName: form.applicantName || "",
  ApplicantGender: form.gender || "",
  ApplicantDob: form.dob || "",
  ApplicantMob: form.mobile || "",
  ApplicantEmail: form.email || "",
  ApplicantDoc: form.identityProof?.join(",") || "",
  ApplicantDocFile: identity1 || "",
  ApplicantDocFile2: identity2 || "",
  ApplicantFather: form.fatherName || "",
  ApplicantMother: form.motherName || "",
  ApplicantAddress: form.address || "",
  ApplicantState: form.stateOfDomicile || "",
  ApplicantDistrict: form.districtOfDomicile || "",
  State2: form.stateOfCollege || "",
  District2: form.districtOfCollege || "",
  SubQualification: form.subQualification || "",
  FundUtiliseOthers: form.useOfFunds === "others" ? form.useOfFundsOther : "",
  IncomeInfo: form.annualIncome || "",
  AlternateMobile: form.alternateMobile || "",
  GuardianMobile: form.guardianMobile || "",
  PassportFile: passportFile || "",
  // Fix the DifferentlyAbled field to be an actual boolean instead of a string
  DifferentlyAbled: form.differentlyAbled === "yes",
  DisabilityCert: form.differentlyAbled === "no" ? "" : disabilityCert || "",
  // Also fix SingleParent field to be boolean
  SingleParent: form.singleParent === "yes",
  ParentsAlive: form.parentsAlive === "yes",
  DomicileCert: domicileCert || "",
  Identity1: identity1 || "",
  Identity2: identity2 || "",
  CityOfCollege: form.cityOfCollege || "",
  YearOfLearning: form.yearOfLearning || "",
  TotalMarks: form.totalMarks || "",
  CollegeId: collegeId || "",
  InternationalPublicationLinks: form.internationalPublicationLinks || "",
  NationalPublicationLinks: form.nationalPublicationLinks || "",
  BankName: form.bankName || "",
  BranchName: form.branchName || "",
  AccountNumber: form.accountNumber || "",
  IfscCode: form.ifscCode || "",
  BankAddress: form.bankAddress || "",
  PanNumber: form.panNumber || "",
  BankDocs: bankDocs || "",
  PanFile: panFile || "",
  Signature: signature || "",
  // Fix Declaration field to be boolean
  // The API has two declaration fields; the first now covers both content declarations
  Declaration: form.declaration && form.declarationVerification,
  // Fix SignatureDeclaration field to be boolean
  SignatureDeclaration: form.signatureDeclaration,
  StateOfCollege: form.stateOfCollege || "",
  StateOfCollegeCode: stateCodes[form.stateOfCollege] || "",
  StateOfDomicile: form.stateOfDomicile || "",
  StateOfDomicileCode: stateCodes[form.stateOfDomicile] || "",
  // Also include a nested Request object to satisfy APIs that expect request.StateOfDomicileCode
 
    StateOfCollegeCode: stateCodes[form.stateOfCollege] || "",
    StateOfDomicileCode: stateCodes[form.stateOfDomicile] || ""
  
};

          console.log("Sending form data to API");

          // Add error handling to see what's going wrong
          try {
            console.log("Sending payload:", JSON.stringify(payload));
            const res = await axiosInstance.post("/api/Student/ScholarshipApplication", payload);
            console.log("Response received:", res);
            
            if (res.data.Status === "200") {
              // Use the reference ID from the API response
              const referenceId = res.data.ReferenceId;
              
              toast.success("Submitted successfully");
              navigate("/success", {
                state: {
                  referenceId,
                  name: form.applicantName,
                  email: form.email,
                  formType: "Scholarship  Application"
                },
              });
            }
          } catch (apiError) {
            console.error("API Error Response:", apiError.response?.data);
            // Handle specific error messages
            if (apiError.response?.data?.Status === "400" && 
                apiError.response?.data?.Message === "You are permitted to submit the scholarship application only once using this email ID.") {
              toast.error("You are permitted to submit the scholarship application only once using this email ID.");
            } else {
              toast.error(apiError.response?.data?.Message || "An error occurred. Please try again.");
            }
          }
        } catch (err) {
          console.error("Submission error:", err);
          toast.error("An error occurred during submission. Please try again.");
        } finally {
          setLoading(false); // End loading state regardless of success or error
          isSubmittingRef.current = false; // Reset submitting state
        }
      }
    } else {
      // Scroll to the first error field
      const firstErrorField = Object.keys(validationErrors).find(
        (key) => validationErrors[key]
      );
      if (firstErrorField) {
        const el = document.getElementsByName(firstErrorField)[0];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus();
        }
      }
      toast.error("Please fill all required fields");
    }
  };
  const renderAcademicSection = () => (
    <div>
      <h2 className="section-header">C: Academic And Other Credentials</h2>
      <div className="row g-3 mb-2">
        {/* College Details */}
        <div className="col-md-3">
          <label className="form-label">
            Name of the Medical college <span className="text-danger">*</span>
          </label>

          <input
            type="text"
            className="form-control"
            name="medicalCollege"
            value={form.medicalCollege}
            onChange={handleChange}
            placeholder="Enter your institute"
            onBlur={(e) => handleBlur(e)}
          />
          {submitted && errors.medicalCollege && (
            <div className="text-danger small">{errors.medicalCollege}</div>
          )}
        </div>
        <div className="col-md-3">
          <label className="form-label">
            State of College <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="stateOfCollege"
            value={form.stateOfCollege || ""}
            onChange={handleChange}
          >
            <option value="">Select State</option>
            {indianStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          {submitted && errors.stateOfCollege && (
            <div className="text-danger small">{errors.stateOfCollege}</div>
          )}
        </div>
        <div className="col-md-3">
          <label className="form-label">
            District of College<span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            name="districtOfCollege"
            value={form.districtOfCollege}
            onChange={handleChange}
            placeholder="Enter district"
          />
          {submitted && errors.districtOfCollege && (
            <div className="text-danger small">{errors.districtOfCollege}</div>
          )}
        </div>
        <div className="col-md-3">
          <label className="form-label">City of College (Medical)</label>
          <input
            type="text"
            className="form-control"
            name="cityOfCollege"
            value={form.cityOfCollege}
            onChange={handleChange}
            placeholder="City of Medical College"
          />
          {submitted && errors.cityOfCollege && (
            <div className="text-danger small">{errors.cityOfCollege}</div>
          )}
        </div>
        {/* Course & Year */}
        <div className="col-md-3">
          <label className="form-label">
            Medical course studying <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="medicalCourse"
            value={form.medicalCourse}
            onChange={handleChange}
          >
            <option value="">Choose from Dropdown</option>
            {/* <option value="Medicalcoursestudying">
              Medical course studying
            </option> */}
            <option value="Undergraduate(MBBS)">Undergraduate (MBBS)</option>
            <option value="MBBSInternship">MBBS Internship</option>
            <option value="Postgraduate(MD/MS/DNB)">
              Postgraduate (MD/MS/DNB)
            </option>
            {/* Add more as needed */}
          </select>
          {submitted && errors.medicalCourse && (
            <div className="text-danger small">{errors.medicalCourse}</div>
          )}
        </div>
        <div className="col-md-3">
          <label className="form-label">
            Select Year of Learning <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="yearOfLearning"
            value={form.yearOfLearning}
            onChange={handleChange}
          >
            <option value="">Select Year</option>
            <option value="1">1st Year</option>
            <option value="2">2nd Year</option>
            <option value="3">3rd Year</option>
            <option value="4">4th Year</option>
            <option value="5">Internships</option>
            <option value="6">PGs (MD/MS and DNB)</option>
            {/* Add more as needed */}
          </select>
          {submitted && errors.yearOfLearning && (
            <div className="text-danger small">{errors.yearOfLearning}</div>
          )}
        </div>
        {/* College ID & Marks */}
        <div className="col-md-3">
          <label className="form-label">
            Upload your College/Hospital ID
            <span className="text-danger">*</span>
          </label>

          <input
            type="file"
            className={`form-control ${errors.collegeId ? 'border-danger' : ''}`}
            name="collegeId"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              handleFileChange(e, setCollegeId, 'collegeId');
              setErrors((prev) => ({ ...prev, collegeId: "" }));
            }}
            placeholder="Upload Medical College / Hospital ID"
          />
          {collegeId && <span className="small text-success">Uploaded ✓</span>}
          {errors.collegeId && (
            <div className="text-danger small">{errors.collegeId}</div>
          )}
        </div>
        {form.yearOfLearning !== "1" && (
        <div className="col-md-3">
          <label className="form-label">
            Total marks of the exam
            <span className="text-danger">*</span>
          </label>

          <input
            type="text"
            className="form-control"
            name="totalMarks"
            value={form.totalMarks}
            onChange={handleChange}
            placeholder="Enter total marks(previous academic year)"
            onBlur={(e) => handleBlur(e)}
          />
          {submitted && errors.totalMarks && (
            <div className="text-danger small">{errors.totalMarks}</div>
          )}
        </div>
        )}
        {/* NEET/Marksheet */}
        {/* 1st year: NEET marks, NEET total and marksheet on one aligned row */}
        {form.yearOfLearning === "1" && <div className="w-100 m-0 p-0"></div>}
        {form.yearOfLearning === "1" && (
          <div className="col-md-3">
            <label className="form-label">
              Enter your NEET Entrance Marks
              <span className="text-danger">*</span>{" "}
            </label>

            <input
              type="text"
              className="form-control"
              name="neetMarks"
              value={form.neetMarks}
              onChange={handleChange}
              placeholder="NEET marks obtained"
              onBlur={(e) => handleBlur(e)}
            />
            {submitted && errors.neetMarks && (
              <div className="text-danger small">{errors.neetMarks}</div>
            )}
          </div>
        )}
        {form.yearOfLearning === "1" && (
          <div className="col-md-3">
            <label className="form-label">
              Total marks of NEET Entrance
              <span className="text-danger">*</span>
            </label>

            <input
              type="text"
              className="form-control"
              name="totalMarks"
              value={form.totalMarks}
              onChange={handleChange}
              placeholder="Enter total marks of NEET exam"
              onBlur={(e) => handleBlur(e)}
            />
            {submitted && errors.totalMarks && (
              <div className="text-danger small">{errors.totalMarks}</div>
            )}
          </div>
        )}
        {/* Marksheet - Only show for non-1st year students */}
        <div className="col-md-6">
          <label className="form-label">
            {form.yearOfLearning === "1" 
              ? "Please upload NEET Entrance Marksheet" 
              : "Please upload marksheet of the previous academic year"}{" "}
            <span className="text-danger">*</span>
          </label>

          <input
            type="file"
            className={`form-control ${errors.marksheet ? 'border-danger' : ''}`}
            name="marksheet"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              handleFileChange(e, setMarksheet, 'marksheet');
              setErrors((prev) => ({ ...prev, marksheet: "" }));
            }}
            placeholder="Upload marksheet"
          />
          {marksheet && <span className="small text-success">Uploaded ✓</span>}
          {errors.marksheet && (
            <div className="text-danger small">{errors.marksheet}</div>
          )}
        </div>
        {/* Marks Achieved - Only show for non-1st year students */}
        {form.yearOfLearning !== "1" && (
        <div className="col-md-6">
          <label className="form-label">
            Marks achieved by you in the previous year{" "}
            <span className="text-danger">*</span>
          </label>

          <input
            type="text"
            className="form-control"
            name="marksAchieved"
            value={form.marksAchieved}
            onChange={handleChange}
            placeholder="Achieved total marks in the last academic year"
            onBlur={(e) => handleBlur(e)}
          />
          {submitted && errors.marksAchieved && (
            <div className="text-danger small">{errors.marksAchieved}</div>
          )}
        </div>
        )}
        {/* Publication Section */}
        {/* --- Publication Section (One Row, Like Image) --- */}
        <h4 className="section-header mt-4">Publication</h4>
        <div className="row mb-2 g-3">
          {/* International Publication */}
          <div className="col-md-6">
            <label className="form-label">
              Do you have any International Publications
              <span className="small text-muted">
                (Select from Dropdown) <span className="text-danger">*</span>
              </span>
            </label>

            <select
              className="form-select"
              name="internationalPublication"
              value={form.internationalPublication}
              onChange={handleChange}
            >
              {" "}
              <option value="">Select from Dropdown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {submitted && errors.internationalPublication && (
              <div className="text-danger small">
                {errors.internationalPublication}
              </div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">
              International Publication
              <span className="small text-muted">
                (Upload up to 7 files only, Kindly upload as ZIP folder){" "}
                <span className="text-danger">*</span>
              </span>
            </label>

            <input
              type="file"
              className={`form-control ${errors.internationalPublicationFile ? 'border-danger' : ''}`}
              name="internationalPublicationFile"
              accept=".zip"
              disabled={form.internationalPublication === "no"}
              onChange={(e) => {
                handleZipFileChange(e, setInternationalPublicationFile, 'internationalPublicationFile');
                setErrors((prev) => ({
                  ...prev,
                  internationalPublicationFile: "",
                }));
              }}
              placeholder="Upload international publications (.zip)"
            />
            {internationalPublicationFile && form.internationalPublication !== "no" && (
              <span className="small text-success">Uploaded ✓</span>
            )}
            {errors.internationalPublicationFile && (
              <div className="text-danger small">
                {errors.internationalPublicationFile}
              </div>
            )}
          </div>
          <div className="col-md-12">
            <label className="form-label big-form-label">
              Please provide links to web pages mentioning citations for each
              publication submitted above.
              <span className="text-danger">*</span>
              <br />
              <span className="small text-muted">
                Each link to be separated by a comma
              </span>
            </label>
            <input
              type="text"
              className="form-control"
              name="internationalPublicationLinks"
              value={
                form.internationalPublicationLinks ||
                (form.internationalPublication === "no" ? "NA" : "")
              }
              onChange={handleChange}
              disabled={form.internationalPublication === "no"}
              placeholder={form.internationalPublication === "no" ? "NA" : ""}
              onBlur={(e) => handleBlur(e)}
            />
            {submitted && errors.internationalPublicationLinks && (
              <div className="text-danger small">
                {errors.internationalPublicationLinks}
              </div>
            )}
          </div>
        </div>

        <div className="row mb-2 g-3">
          {/* National Publication */}
          <div className="col-md-4">
            <label className="form-label  big-form-label">
              Do you have any National Publications
              <span className="text-danger">*</span>
              <br />
              <span className="small text-muted">(Select from Dropdown)</span>
            </label>
            <select
              className="form-select"
              name="nationalPublication"
              value={form.nationalPublication}
              onChange={handleChange}
            >
              <option value="">Select from Dropdown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {submitted && errors.nationalPublication && (
              <div className="text-danger small">
                {errors.nationalPublication}
              </div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label big-form-label">
              National Publication
              <span className="small text-muted">
                (Upload up to 7 files only, Kindly upload as ZIP folder){" "}
                <span className="text-danger">*</span>
              </span>
            </label>

            <input
              type="file"
              className={`form-control ${errors.nationalPublicationFile ? 'border-danger' : ''}`}
              name="nationalPublicationFile"
              accept=".zip"
              disabled={form.nationalPublication === "no"}
              onChange={(e) => {
                handleZipFileChange(e, setNationalPublicationFile, 'nationalPublicationFile');
                setErrors((prev) => ({
                  ...prev,
                  nationalPublicationFile: "",
                }));
              }}
              placeholder="Upload national publications (.zip)"
            />
            {nationalPublicationFile && form.nationalPublication !== "no" && (
              <span className="small text-success">Uploaded ✓</span>
            )}
            {errors.nationalPublicationFile && (
              <div className="text-danger small">
                {errors.nationalPublicationFile}
              </div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label  big-form-label">
              Please provide web links for Publication publication submitted
              <span className="text-danger">*</span>
              <br />
              <span className="small text-muted">
                Each link to be separated by a comma
              </span>
            </label>
            <input
              type="text"
              className="form-control"
              name="nationalPublicationLinks"
              value={
                form.nationalPublicationLinks ||
                (form.nationalPublication === "no" ? "NA" : "")
              }
              onChange={handleChange}
              disabled={form.nationalPublication === "no"}
              placeholder={form.nationalPublication === "no" ? "NA" : ""}
              onBlur={(e) => handleBlur(e)}
            />
            {submitted && errors.nationalPublicationLinks && (
              <div className="text-danger small">
                {errors.nationalPublicationLinks}
              </div>
            )}
          </div>
        </div>
        {/* National Publication */}

        {/* Presentation */}
        <h4 className="section-header">Presentation</h4>
        <div className="row mb-2 g-3">
          {/* Paper and Poster Presentation */}
          <div className="col-12 col-md-6">
            <label className="form-label">
              You Have any Paper and Poster Presentation
              <span className="text-danger">*</span>{" "}
              <span className="small text-muted">(Select from dropdown)</span>
            </label>
            <select
              className="form-select"
              name="presentation"
              value={form.presentation}
              onChange={handleChange}
            >
              {" "}
              <option value="">Select from Dropdown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {submitted && errors.presentation && (
              <div className="text-danger small">{errors.presentation}</div>
            )}
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">
              Please upload Paper and Poster Presentation
              <span className="small text-muted">
                (Upload up to 7 files only, Kindly upload as ZIP folder){" "}
                <span className="text-danger">*</span>{" "}
              </span>
            </label>
            <input
              type="file"
              className={`form-control ${errors.presentationFile ? 'border-danger' : ''}`}
              name="presentationFile"
              accept=".zip"
              disabled={form.presentation === "no"}
              onChange={(e) => {
                handleZipFileChange(e, setPresentationFile, 'presentationFile');
                setErrors((prev) => ({ ...prev, presentationFile: "" }));
              }}
              placeholder="Upload paper and poster presentations (.zip)"
            />
            {presentationFile && form.presentation !== "no" && (
              <span className="small text-success">Uploaded ✓</span>
            )}
            {errors.presentationFile && (
              <div className="text-danger small">{errors.presentationFile}</div>
            )}
          </div>
        </div>
        {/* Academic Non-Academic Awards */}
        <div className="row g-3 mb-2 align-items-end">
          {/* Paper and Poster Presentation */}
          <div className="col-12 col-md-6">
            <label className="form-label">
              Do you have any Academic/Non-Academic awards
              <span className="small text-muted">
                (Select from Dropdown) <span className="text-danger">*</span>
              </span>
            </label>

            <select
              className="form-select"
              name="awards"
              value={form.awards}
              onChange={handleChange}
            >
              <option value="">Select from Dropdown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {submitted && errors.awards && (
              <div className="text-danger small">{errors.awards}</div>
            )}
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">
              Please upload Academic/Non-Academic awards
              <span className="small text-muted">
                (Please upload a ZIP folder containing all files. All documents in
                this form together can be up to 10 MB) <span className="text-danger">*</span>
              </span>
            </label>

            <input
              type="file"
              className={`form-control ${errors.awardsFile ? 'border-danger' : ''}`}
              name="awardsFile"
              accept=".zip"
              disabled={form.awards === "no"}
              onChange={handleAwardsFileChange}
              placeholder="Upload academic/non-academic awards (.zip)"
            />
            {awardsFile.length > 0 && form.awards !== "no" && (
              <span className="small text-success">Uploaded ✓</span>
            )}
            {errors.awardsFile && (
              <div className="text-danger small">{errors.awardsFile}</div>
            )}
          </div>
        </div>
        {/* Extra-Curricular Achievements */}
        <div className="row g-3 mb-2 align-items-end">
          {/* Paper and Poster Presentation */}
          <div className="col-12 col-md-6">
            <label className="form-label">
              Do you have any Extra-Curricular Achievements
              <span className="small text-muted">
                (Select from Dropdown) <span className="text-danger">*</span>
              </span>
            </label>

            <select
              className="form-select"
              name="extraCurricular"
              value={form.extraCurricular}
              onChange={handleChange}
            >
              <option value="">Select from Dropdown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {submitted && errors.extraCurricular && (
              <div className="text-danger small">{errors.extraCurricular}</div>
            )}
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">
              Extra-Curricular Achievements
              <span className="small text-muted">
                (Please upload a ZIP folder containing all files. All documents in
                this form together can be up to 10 MB) <span className="text-danger">*</span>
              </span>
            </label>

            <input
              type="file"
              className={`form-control ${errors.extraCurricularFile ? 'border-danger' : ''}`}
              name="extraCurricularFile"
              accept=".zip"
              disabled={form.extraCurricular === "no"}
              onChange={handleExtraCurricularFileChange}
              placeholder="Upload extra-curricular achievements (.zip)"
            />
            {extraCurricularFile.length > 0 && form.extraCurricular !== "no" && (
              <span className="small text-success">Uploaded ✓</span>
            )}
            {errors.extraCurricularFile && (
              <div className="text-danger small">
                {errors.extraCurricularFile}
              </div>
            )}
          </div>
        </div>
        {/* Community Services */}
        <div className="row g-3 mb-2 align-items-end">
          {/* Paper and Poster Presentation */}
          <div className="col-12 col-md-6">
            <label className="form-label">
              Do you have done any Community services
              <span className="small text-muted">
                (Select from Dropdown) <span className="text-danger">*</span>
              </span>
            </label>

            <select
              className="form-select"
              name="communityService"
              value={form.communityService}
              onChange={handleChange}
            >
              <option value="">Select from Dropdown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {submitted && errors.communityService && (
              <div className="text-danger small">{errors.communityService}</div>
            )}
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">
              Community services done
              <span className="small text-muted">
                (Please upload a ZIP folder containing all files. All documents in
                this form together can be up to 10 MB) <span className="text-danger">*</span>
              </span>
            </label>

            <input
              type="file"
              className={`form-control ${errors.communityServiceFile ? 'border-danger' : ''}`}
              name="communityServiceFile"
              accept=".zip"
              disabled={form.communityService === "no"}
              onChange={handleCommunityServiceFileChange}
              placeholder="Upload community service documents (.zip)"
            />
            {communityServiceFile.length > 0 && form.communityService !== "no" && (
              <span className="small text-success">Uploaded ✓</span>
            )}
            {errors.communityServiceFile && (
              <div className="text-danger small">
                {errors.communityServiceFile}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Bank Account Details */}
      <div className="mt-4">
        <h4 className="section-header"> Bank Account Details</h4>

        <div className="row g-3 mb-2">
          <div className="col-md-4">
            <label className="form-label">
              Name as in Bank<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="bankName"
              value={form.bankName}
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              placeholder="Enter name as per bank records"
            />
            {submitted && errors.bankName && (
              <div className="text-danger small">{errors.bankName}</div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Branch Name<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="branchName"
              value={form.branchName}
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              placeholder="Enter bank branch name"
            />
            {submitted && errors.branchName && (
              <div className="text-danger small">{errors.branchName}</div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Account Number<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="accountNumber"
              value={form.accountNumber}
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              placeholder="Enter your bank account number"
            />
            {submitted && errors.accountNumber && (
              <div className="text-danger small">{errors.accountNumber}</div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">
              IFSC Code<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="ifscCode"
              value={form.ifscCode}
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              placeholder="Enter IFSC code (e.g., SBIN0001234)"
            />
            {submitted && errors.ifscCode && (
              <div className="text-danger small">{errors.ifscCode}</div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">
              Bank Address<span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="bankAddress"
              value={form.bankAddress}
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              placeholder="Enter complete bank branch address"
            />
            {submitted && errors.bankAddress && (
              <div className="text-danger small">{errors.bankAddress}</div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">PAN Number<span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="panNumber"
              value={form.panNumber}
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              placeholder="Enter PAN number"
            />
            {submitted && errors.panNumber && (
              <div className="text-danger small">{errors.panNumber}</div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">Upload Signature<span className="text-danger">*</span></label>
            <input
              type="file"
              className={`form-control ${errors.signature ? 'border-danger' : ''}`}
              name="signature"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                handleFileChange(e, setSignature, 'signature');
                setErrors((prev) => ({ ...prev, signature: "" }));
              }}
              placeholder="Upload signature (.pdf, .jpg, .jpeg, .png)"
            />
            {signature && <span className="small text-success">Uploaded ✓</span>}
            {errors.signature && (
              <div className="text-danger small">{errors.signature}</div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">Bank passbook<span className="text-danger">*</span></label>
            <input
              type="file"
              className={`form-control ${errors.bankDocs ? 'border-danger' : ''}`}
              name="bankDocs"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                handleFileChange(e, setBankDocs, 'bankDocs');
                setErrors((prev) => ({ ...prev, bankDocs: "" }));
              }}
              placeholder="Upload bank passbook (.pdf, .jpg, .jpeg, .png)"
            />
            {bankDocs && <span className="small text-success">Uploaded ✓</span>}
            {errors.bankDocs && (
              <div className="text-danger small">{errors.bankDocs}</div>
            )}
          </div>
          <div className="col-md-4">
            <label className="form-label">PAN Card<span className="text-danger">*</span></label>
            <input
              type="file"
              className={`form-control ${errors.panFile ? 'border-danger' : ''}`}
              name="panFile"
              accept=".pdf,.jpg,.jpeg,.png"
              placeholder="Upload PAN card (.pdf, .jpg, .jpeg, .png)"
              onChange={(e) => {
                handleFileChange(e, setPanFile, 'panFile');
                setErrors((prev) => ({ ...prev, panFile: "" }));
              }}
            />
            {panFile && <span className="small text-success">Uploaded ✓</span>}
            {errors.panFile && (
              <div className="text-danger small">{errors.panFile}</div>
            )}
          </div>
        </div>
        <div className="col-12 mt-2">
          <span className="small text-success">
            Note: Bank Details will be used only when candidates win the
            scholarship. The student will be informed of their winning and post
            their win, funds would be transferred. The information will be used
            only for this scholarship.
          </span>
        </div>
      </div>
      {/* Declaration */}
      <div className="mt-4">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            name="declaration"
            checked={form.declaration}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, declaration: e.target.checked }))
            }
          />
          <label className="form-check-label">
            I declare that all information and documents submitted in this
            application are true, complete and my own.
          </label>
          {submitted && errors.declaration && (
            <div className="text-danger small">{errors.declaration}</div>
          )}
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            name="declarationVerification"
            checked={form.declarationVerification}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                declarationVerification: e.target.checked,
              }))
            }
          />
          <label className="form-check-label">
            I understand that MedEngage / Metropolis Foundation may verify my
            details with my college and the issuing authorities, and that any false
            or misleading information will lead to disqualification and, if funds
            have been paid, recovery of the amount.
          </label>
          {submitted && errors.declarationVerification && (
            <div className="text-danger small">
              {errors.declarationVerification}
            </div>
          )}
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            name="signatureDeclaration"
            checked={form.signatureDeclaration}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                signatureDeclaration: e.target.checked,
              }))
            }
          />
          <label className="form-check-label">
            I confirm that I have attached my signature and agree to the above
            declarations.
          </label>
          {submitted && errors.signatureDeclaration && (
            <div className="text-danger small">
              {errors.signatureDeclaration}
            </div>
          )}
        </div>
      </div>
      <div class="alert alert-info d-flex align-items-center" role="alert">
        <i class="fa fa-sticky-note me-2" aria-hidden="true"></i>
        <strong>Note:</strong> Please review the form carefully before
        submitting and please be patient as scholorship form may take time to
        get submitted due to attachments.
      </div>
      {/* Buttons */}
      <div className="d-flex flex-column flex-sm-row justify-content-center gap-3 mt-3">
        <button
          type="button"
          className="btn btn-secondary rounded-3 me-1"
          onClick={() => setStep(1)}
        >
          Back
        </button>
        <button
          type="button" // Changed from submit to button to handle our own submission
          className="btn btn-outline-success submit-btn rounded-3"
          disabled={loading}
          onClick={(e) => {
            // Trim all string fields before submission
            const updatedForm = {};
            let hasChanges = false;

            Object.keys(form).forEach((key) => {
              if (
                typeof form[key] === "string" &&
                form[key].trim() !== form[key]
              ) {
                updatedForm[key] = form[key].trim();
                hasChanges = true;
              }
            });

            if (hasChanges) {
              setForm((prev) => ({
                ...prev,
                ...updatedForm,
              }));

              // Allow time for state update before validation and submission
              setTimeout(() => {
                handleSubmit(
                  new Event("submit", { cancelable: true, bubbles: true })
                );
              }, 100);
            } else {
              // No spaces to trim, just submit the form
              handleSubmit(
                new Event("submit", { cancelable: true, bubbles: true })
              );
            }
          }}
        >
          {loading ? (
            <span>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Submitting...
            </span>
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </div>
  );


  // If applications are closed, don't render the form — show an info alert (same UX as Internship)
  if (!isScholarshipActive) {
    return (
      <div className="container-fluid py-5">
        <div className="container">
          <div className="alert alert-info mb-0">
            Applications are currently closed. Please check back later.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-5">
      {/* {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 200 }}
        >
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )} */}
      <div className="form-container">
        <Popup />
        <form onSubmit={handleSubmit} noValidate>
          {step === 1 ? (
            // ...your existing Section A & B code here...
            <>
              <h2 className="text-center mt-2 form-heading">
                <span className="green">Scholarship Application</span> MedEngage
                2.0
              </h2>
              {/* <form onSubmit={handleSubmit} noValidate> */}
              {/* A: Background */}
              <h2 className="section-header">A: Background</h2>
              <div className="row g-3 mb-2 align-items-end">
                <div className="col-md-4">
                  <label className="form-label">
                    Have you received the scholarship by MedEngage in the past?
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="receivedScholarship"
                    value={form.receivedScholarship || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select From Dropdown</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {submitted && errors.receivedScholarship && (
                    <div className="text-danger small">
                      {errors.receivedScholarship}
                    </div>
                  )}
                </div>

                {form.receivedScholarship === "yes" && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">
                        If yes, which year did you apply for the scholarship?
                        <span className="text-danger">*</span>
                      </label>

                      <select
                        className="form-select"
                        name="yearOfWinning"
                        value={form.yearOfWinning || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Year</option>
                        <option value="2021">2021</option>
                        <option value="2022">2022</option>
                        <option value="2023">2023</option>
                        <option value="2024">2024</option>
                        <option value="2025-26">2025-26</option>
                        <option value="Other">Other</option>
                      </select>
                      {submitted && errors.yearOfWinning && (
                        <div className="text-danger small">
                          {errors.yearOfWinning}
                        </div>
                      )}
                    </div>

                    {form.yearOfWinning === "Other" && (
                      <div className="col-md-4">
                        <label className="form-label">
                          Please enter the year
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="yearOfWinningOther"
                          placeholder="Enter year"
                          value={form.yearOfWinningOther}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {submitted && errors.yearOfWinningOther && (
                          <div className="text-danger small">
                            {errors.yearOfWinningOther}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="col-md-4">
                      <label className="form-label">
                        How have you utilized the scholarship funds?
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        name="fundUtilization"
                        value={form.fundUtilization || ""}
                        onChange={handleChange}
                      >
                        <option value="">Choose from Dropdown</option>
                        <option value="books">Buy Books</option>
                        <option value="fees">
                          Attend seminars / workshop , both national and
                          International
                        </option>
                        <option value="Pay tuition fees">
                          Pay tuition fees
                        </option>
                        <option value="Supplementary courses">
                          Supplementary courses
                        </option>
                        {/* <option value="other">Others ( Please specify)</option> */}
                      </select>
                      {submitted && errors.fundUtilization && (
                        <div className="text-danger small">
                          {errors.fundUtilization}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="row g-3 mb-2">
                <div className="col-12 col-md-6">
                  <label className="form-label">
                    How will you use scholarship funds (if you win)?
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="useOfFunds"
                    value={form.useOfFunds}
                    onChange={handleChange}
                  >
                    <option value="">How will you use scholarship funds</option>
                    <option value="books">Buy books</option>
                    <option value="seminars">
                      Attend seminars / workshop, both national and
                      International
                    </option>
                    <option value="tuition">Pay tuition fees</option>
                    <option value="supplementary">Supplementary courses</option>
                   {/*  <option value="others">Others (Please specify)</option> */}
                  </select>
                  {submitted && errors.useOfFunds && (
                    <div className="text-danger small">{errors.useOfFunds}</div>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">
                    How many of your siblings pursuing medical studies have
                    received the MedEngage scholarship in the past?{" "}
                    {/* <span className="text-danger">*</span> */}
                  </label>

                  <select
                    className="form-select"
                    name="siblingsScholarship"
                    value={form.siblingsScholarship}
                    onChange={handleChange}
                  >
                    <option value="">Select From Dropdown</option>
                    <option value="0">0</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                  </select>
                  {submitted && errors.siblingsScholarship && (
                    <div className="text-danger small">
                      {errors.siblingsScholarship}
                    </div>
                  )}
                </div>
              </div>
              {form.siblingsScholarship === "1" && (
                <div className="row g-3 mb-2">
                  <div className="col-12 col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      name="siblingOneName" // Changed from SiblingOneName to siblingOneName
                      placeholder="Sibling 1 Name (Upto 50 characters)"
                      maxLength={50}
                      value={form.siblingOneName || ""}
                      onChange={handleChange}
                    />
                    {submitted && errors.siblingOneName && (
                      <div className="text-danger small">
                        {errors.siblingOneName}
                      </div>
                    )}
                  </div>
                  <div className="col-12 col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      name="siblingOneCollege"
                      placeholder="Sibling 1 College Name (Upto 100 characters)"
                      maxLength={100}
                      value={form.siblingOneCollege || ""}
                      onChange={handleChange}
                      onBlur={(e) => handleBlur(e)}
                    />
                    {submitted && errors.siblingOneCollege && (
                      <div className="text-danger small">
                        {errors.siblingOneCollege}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {form.siblingsScholarship === "2" && (
                <>
                  <div className="row g-3 mb-2">
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        name="siblingOneName"
                        placeholder="Sibling 1 Name (Upto 50 characters)"
                        maxLength={50}
                        value={form.siblingOneName || ""}
                        onChange={handleChange}
                        onBlur={(e) => handleBlur(e)}
                      />
                      {submitted && errors.siblingOneName && (
                        <div className="text-danger small">
                          {errors.siblingOneName}
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        name="siblingOneCollege"
                        placeholder="Sibling 1 College Name (Upto 100 characters)"
                        maxLength={100}
                        value={form.siblingOneCollege || ""}
                        onChange={handleChange}
                        onBlur={(e) => handleBlur(e)}
                      />
                      {submitted && errors.siblingOneCollege && (
                        <div className="text-danger small">
                          {errors.siblingOneCollege}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row g-3 mb-2">
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        name="siblingTwoName"
                        placeholder="Sibling 2 Name (Upto 50 characters)"
                        maxLength={50}
                        value={form.siblingTwoName || ""}
                        onChange={handleChange}
                        onBlur={(e) => handleBlur(e)}
                      />
                      {submitted && errors.siblingTwoName && (
                        <div className="text-danger small">
                          {errors.siblingTwoName}
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        name="siblingTwoCollege"
                        placeholder="Sibling 2 College Name (Upto 100 characters)"
                        maxLength={100}
                        value={form.siblingTwoCollege || ""}
                        onChange={handleChange}
                        onBlur={(e) => handleBlur(e)}
                      />
                      {submitted && errors.siblingTwoCollege && (
                        <div className="text-danger small">
                          {errors.siblingTwoCollege}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* B: Applicant Personal Details */}
              <div className="row align-items-center mb-1">
                <div className="col-md-12">
                  <h2 className="section-header mt-3 mb-0">
                    B: Applicant Personal Details
                  </h2>
                </div>
                <div className="col-12 col-md-6"></div>
              </div>
              <div className="row g-4">
                <div className="col-md-4">
                  <label className="form-label">
                    Applicant Name<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={50}
                    name="applicantName"
                    placeholder="Applicant Name"
                    value={form.applicantName}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e)}
                  />
                  {submitted && errors.applicantName && (
                    <div className="text-danger small">
                      {errors.applicantName}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Select Gender <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="gender"
                    value={form.gender}
                    onChange={handleChange}
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="transGender">TransGender</option>
                    <option value="other">other</option>
                  </select>
                  {submitted && errors.gender && (
                    <div className="text-danger small">{errors.gender}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Date of Birth <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="dob"
                    placeholder="Enter Date of Birth"
                    value={form.dob}
                    onChange={(e) => {
                      handleChange(e);
                      setErrors((prev) => ({ ...prev, dob: "" }));
                    }}
                  />
                  {submitted && errors.dob && (
                    <div className="text-danger small">{errors.dob}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Applicant Mobile <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="mobile"
                    placeholder="Applicant Mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e)}
                  />
                  {submitted && errors.mobile && (
                    <div className="text-danger small">{errors.mobile}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Applicant Email{" "}
                    <small class="text-muted me-1">
                      (This form can be submitted only once using this email ID)
                    </small>
                    <span className="required">*</span>
                  </label>

                  <input
                    type="email"
                    className="form-control"
                    maxLength={80}
                    name="email"
                    placeholder="Applicant Email"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e)}
                  />
                  {submitted && errors.email && (
                    <div className="text-danger small">{errors.email}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Select Identity proof <span className="text-danger">*</span>
                  </label>
                  <Select
                    isMulti
                    name="identityProof"
                    options={identityProofOptions}
                    classNamePrefix="react-select"
                    value={identityProofOptions.filter((opt) =>
                      form.identityProof?.includes(opt.value)
                    )}
                    onChange={(selected) => {
                      setForm((prev) => ({
                        ...prev,
                        identityProof: selected
                          ? selected.map((opt) => opt.value)
                          : [],
                      }));
                      setErrors((prev) => ({ ...prev, identityProof: "" }));
                    }}
                    placeholder="Select Proof of Documents"
                  />
                  <div className="d-flex justify-content-between">
                    <span className="small text-success">
                      (Select at least Two Docs)
                    </span>
                    {submitted && errors.identityProof && (
                      <span className="small text-danger">
                        {errors.identityProof}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Upload Proof of Identity 1{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${
                      errors.identity1 ? "border-danger" : ""
                    }`}
                    name="identity1"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      handleFileChange(e, setIdentity1, "identity1");
                      setErrors((prev) => ({ ...prev, identity1: "" }));
                    }}
                    placeholder="Upload proof of identity 1 (.pdf, .jpg, .jpeg, .png)"
                  />
                  {identity1 && <span className="small text-success">Uploaded ✓</span>}
                  {errors.identity1 && (
                    <div className="text-danger small">{errors.identity1}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Upload Proof of Identity 2{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${
                      errors.identity2 ? "border-danger" : ""
                    }`}
                    name="identity2"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      handleFileChange(e, setIdentity2, "identity2");
                      setErrors((prev) => ({ ...prev, identity2: "" }));
                    }}
                    placeholder="Upload proof of identity 2 (.pdf, .jpg, .jpeg, .png)"
                  />
                  {identity2 && <span className="small text-success">Uploaded ✓</span>}
                  {errors.identity2 && (
                    <div className="text-danger small">{errors.identity2}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Father/Guardian Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="fatherName"
                    placeholder="Father/Guardian Name"
                    value={form.fatherName}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e)}
                  />
                  {submitted && errors.fatherName && (
                    <div className="text-danger small">{errors.fatherName}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Mother/Guardian Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="motherName"
                    placeholder="Mother/Guardian Name"
                    value={form.motherName}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e)}
                  />
                  {submitted && errors.motherName && (
                    <div className="text-danger small">{errors.motherName}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Applicant Address (Permanent){" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="address"
                    placeholder="Applicant Address (Permanent)"
                    value={form.address}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e)}
                  />
                  {submitted && errors.address && (
                    <div className="text-danger small">{errors.address}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Upload Passport Size Photo{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${
                      errors.passportFile ? "border-danger" : ""
                    }`}
                    name="passportFile"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      handleFileChange(e, setPassportFile, "passportFile");
                      setErrors((prev) => ({ ...prev, passportFile: "" }));
                    }}
                    placeholder="Upload passport size photo (.pdf, .jpg, .jpeg, .png)"
                  />
                  {passportFile && (
                    <span className="small text-success">Uploaded ✓</span>
                  )}
                  {errors.passportFile && (
                    <div className="text-danger small">
                      {errors.passportFile}
                    </div>
                  )}
                </div>

                {/* Differently Abled */}
                <div className="col-md-4">
                  <label className="form-label">
                    Are you differently abled (disabled)? If yes, please upload
                    the certificate (government/others){" "}
                    {/* <span className="text-danger">*</span> */}
                  </label>
                  <select
                    className="form-select"
                    name="differentlyAbled"
                    value={form.differentlyAbled}
                    onChange={handleChange}
                  >
                    <option value="">Select an option</option>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {submitted && errors.differentlyAbled && (
                    <div className="text-danger small">
                      {errors.differentlyAbled}
                    </div>
                  )}

                  {form.differentlyAbled === "yes" && (
                    <div className="col-md-12 mt-2">
                      <label className="form-label">Upload Certificate</label>
                      <input
                        type="file"
                        className={`form-control ${
                          errors.disabilityCert ? "border-danger" : ""
                        }`}
                        name="disabilityCert"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          handleFileChange(
                            e,
                            setDisabilityCert,
                            "disabilityCert"
                          );
                          setErrors((prev) => ({
                            ...prev,
                            disabilityCert: "",
                          }));
                        }}
                        placeholder="Upload disability certificate (.pdf, .jpg, .jpeg, .png)"
                      />
                      {disabilityCert && (
                        <div className="small mt-1 text-success">Uploaded ✓</div>
                      )}
                      {errors.disabilityCert && (
                        <div className="text-danger small">
                          {errors.disabilityCert}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Single Parent */}
                <div className="col-md-4">
                  <label className="form-label">
                    Are you a single parent?{" "}
                    {/* <span className="text-danger">*</span> */}
                  </label>
                  <select
                    className="form-select"
                    name="singleParent"
                    value={form.singleParent}
                    onChange={handleChange}
                  >
                    <option value="">Select an option</option>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {submitted && errors.singleParent && (
                    <div className="text-danger small">
                      {errors.singleParent}
                    </div>
                  )}
                </div>

                {/* Parents Alive */}
                <div className="col-md-4">
                  <label className="form-label">
                    Are your parents alive?{" "}
                    {/* <span className="text-danger">*</span> */}
                  </label>
                  <select
                    className="form-select"
                    name="parentsAlive"
                    value={form.parentsAlive}
                    onChange={handleChange}
                  >
                    <option value="">Select an option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {submitted && errors.parentsAlive && (
                    <div className="text-danger small">
                      {errors.parentsAlive}
                    </div>
                  )}
                </div>

                {/* Alternate Mobile (separate row) */}

                <div className="col-md-4">
                  <label className="form-label">
                    Applicant Alternate Mobile Number (for Scholarship
                    Communication) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Mobile Number"
                    name="alternateMobile"
                    value={form.alternateMobile}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e)}
                  />
                  {submitted && errors.alternateMobile && (
                    <div className="text-danger small">
                      {errors.alternateMobile}
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Guardian's Mobile Number{" "}
                    {/* <span className="text-danger">*</span> */}
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Mobile Number"
                    name="guardianMobile"
                    value={form.guardianMobile}
                    onChange={handleChange}
                    onBlur={(e) => handleBlur(e)}
                  />
                  {submitted && errors.guardianMobile && (
                    <div className="text-danger small">
                      {errors.guardianMobile}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    State of Domicile <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="stateOfDomicile"
                    value={form.stateOfDomicile || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select State</option>
                    {indianStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  {submitted && errors.stateOfDomicile && (
                    <div className="text-danger small">
                      {errors.stateOfDomicile}
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    District of Domicile <span className="text-danger">*</span>{" "}
                    <a
                      href="https://www.med-engage.com/wp-content/uploads/2025/07/List-of-112-Aspirational-Districts-in-word.pdf"
                      target="_blank"
                      rel="noopener"
                      className="text-success"
                    >
                      (See Aspirational Districts)
                    </a>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="districtOfDomicile"
                    value={form.districtOfDomicile}
                    onChange={handleChange}
                    placeholder="Enter district"
                  />
                  {submitted && errors.districtOfDomicile && (
                    <div className="text-danger small">
                      {errors.districtOfDomicile}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Domicile Certificate (obtained from government authority /
                    Tahsil / Panchayat or Voter ID card)
                    <span className="text-danger">*</span>
                  </label>

                  <input
                    type="file"
                    className={`form-control ${
                      errors.domicileCert ? "border-danger" : ""
                    }`}
                    name="domicileCert"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      handleFileChange(e, setDomicileCert, "domicileCert");
                      setErrors((prev) => ({ ...prev, domicileCert: "" }));
                    }}
                    placeholder="Upload domicile certificate (.pdf, .jpg, .jpeg, .png)"
                  />
                  {domicileCert && (
                    <span className="small text-success">Uploaded ✓</span>
                  )}
                  {errors.domicileCert && (
                    <div className="text-danger small">
                      {errors.domicileCert}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Income of the family in FY2025-26{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="annualIncome"
                    value={form.annualIncome}
                    onChange={handleChange}
                  >
                    <option value="">Select Annual Income</option>
                    <option value="up to 2 Lakhs">up to 2 Lakhs</option>
                    <option value="=2 upto 3 Lakhs">=2 upto 3 Lakhs</option>
                    <option value="=3 upto 5 Lakhs">=3 upto 5 Lakhs</option>
                    <option value="=5 Lakhs upto 8 Lakhs">
                      =5 Lakhs upto 8 Lakhs
                    </option>
                    <option value="Above 8 Lakhs ">Above 8 Lakhs </option>
                  </select>
                  {submitted && errors.annualIncome && (
                    <div className="text-danger small">
                      {errors.annualIncome}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Upload income certificate (Tahsil / Panchayat) / Form 16 / Form 16A /
                    ITR / ration card / EWS card for family income of FY 2025–26
                    <span className="text-danger">*</span>
                  </label>

                  <input
                    type="file"
                    className={`form-control ${
                      errors.incomeCert ? "border-danger" : ""
                    }`}
                    name="incomeCert"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      handleFileChange(e, setIncomeCert, "incomeCert");
                      setErrors((prev) => ({ ...prev, incomeCert: "" }));
                    }}
                    placeholder="Upload income certificate / Form 16 / ITR / ration or EWS card (.pdf, .jpg, .jpeg, .png)"
                  />
                  {incomeCert && (
                    <span className="small text-success">Uploaded ✓</span>
                  )}
                  {errors.incomeCert && (
                    <div className="text-danger small">{errors.incomeCert}</div>
                  )}
                </div>
                <div className="col-12">
                  <span className="small text-success">
                    (Note: Please upload file less than 10MB and suppoted file
                    extension pdf, word, jpeg, jpg, png)
                  </span>
                </div>
              </div>
              <div className="d-flex justify-content-center mt-2">
                <button
                  type="button" // Changed from submit to button to handle our own logic
                  className="btn btn-outline-success submit-btn rounded-3"
                  disabled={loading}
                  onClick={(e) => {
                    // Trim all string fields before submission
                    const updatedForm = {};
                    let hasChanges = false;

                    Object.keys(form).forEach((key) => {
                      if (
                        typeof form[key] === "string" &&
                        form[key].trim() !== form[key]
                      ) {
                        updatedForm[key] = form[key].trim();
                        hasChanges = true;
                      }
                    });

                    if (hasChanges) {
                      setForm((prev) => ({
                        ...prev,
                        ...updatedForm,
                      }));

                      // Allow time for state update before validation
                      setTimeout(() => {
                        // Run validation for step 1
                        const errors = validate(1);
                        setErrors(errors);
                        setSubmitted(true);

                        // If validation passes, move to step 2
                        if (Object.keys(errors).length === 0) {
                          console.log("Moving to step 2");
                          setStep(2);
                          window.scrollTo(0, 0);
                        } else {
                          console.log("Step 1 validation errors:", errors);
                          // Scroll to the first error
                          const firstErrorField = Object.keys(errors)[0];
                          const el =
                            document.getElementsByName(firstErrorField)[0];
                          if (el) {
                            el.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                            el.focus();
                          }
                        }
                      }, 100);
                    } else {
                      // No spaces to trim, just submit the form
                      const errors = validate(1);
                      setErrors(errors);
                      setSubmitted(true);

                      if (Object.keys(errors).length === 0) {
                        console.log("Moving to step 2");
                        setStep(2);
                        window.scrollTo(0, 0);
                      } else {
                        console.log("Step 1 validation errors:", errors);
                        // Scroll to the first error
                        const firstErrorField = Object.keys(errors)[0];
                        const el =
                          document.getElementsByName(firstErrorField)[0];
                        if (el) {
                          el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                          el.focus();
                        }
                      }
                    }
                  }}
                >
                  {loading ? (
                    <span>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </span>
                  ) : (
                    "Next"
                  )}
                </button>
              </div>
            </>
          ) : (
            renderAcademicSection()
          )}
        </form>
      </div>
    </div>
  );
};

export default ScholarshipForm;